/**
 * Ce qui est commandable, et quand.
 *
 * La boutique ferme entre le marché et l'inventaire parce que le stock FRAIS
 * est alors inconnu. Ce raisonnement ne vaut pas pour tout : l'épicerie se
 * garde, et une précommande de pain ne touche pas au stock du tout — elle est
 * bornée par sa propre heure limite. « Fermé » ne veut donc pas dire « rien » :
 * les rayons configurés (`always_open_category_ids`, déjà étendus aux
 * sous-catégories par le serveur) et les précommandes restent commandables.
 *
 * Le serveur applique exactement la même règle à la commande — ceci n'est que
 * l'affichage.
 */

import type { Product } from '../types/ecommerce.types';
import type { OrderingState } from '../api/ecommerce.api';

/** « samedi 29 août à 18:00 », en heure belge — le client ne lit pas de l'ISO. */
export function formatMomentBelge(iso: string): string {
  try {
    const d = new Date(iso);
    const jour = d.toLocaleDateString('fr-BE', {
      timeZone: 'Europe/Brussels',
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    const heure = d.toLocaleTimeString('fr-BE', {
      timeZone: 'Europe/Brussels',
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${jour} à ${heure}`;
  } catch {
    return iso;
  }
}

/** Le jour civil belge d'un instant, « 2026-10-07 » — pour comparer des jours. */
function jourBelge(d: Date): string {
  return d.toLocaleDateString('fr-CA', { timeZone: 'Europe/Brussels' });
}

/**
 * « dès mercredi » — pour dire quand un article redeviendra commandable.
 * Le jour même, le nom du jour ne dit rien (« dès mercredi » lu un mercredi
 * matin, 07/10/2026) : on donne l'heure, « dès 18h ». La veille : « dès demain ».
 */
export function formatJourReouverture(iso?: string, maintenant: Date = new Date()): string {
  if (!iso) return 'aux jours d’ouverture';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'aux jours d’ouverture';
    const jour = jourBelge(d);
    if (jour === jourBelge(maintenant)) {
      const h = d.toLocaleTimeString('fr-BE', { timeZone: 'Europe/Brussels', hour: '2-digit', minute: '2-digit', hour12: false });
      return `dès ${heureLisible(h.replace('h', ':').replace('.', ':'))}`;
    }
    const demain = new Date(maintenant.getTime() + 24 * 3600 * 1000);
    if (jour === jourBelge(demain)) return 'dès demain';
    return `dès ${d.toLocaleDateString('fr-BE', { timeZone: 'Europe/Brussels', weekday: 'long' })}`;
  } catch {
    return 'aux jours d’ouverture';
  }
}

type OrderableProduct = Pick<Product, 'preorder_enabled' | 'category_id'> & { is_deposit?: boolean };

export function isOrderableNow(product: OrderableProduct, ordering: OrderingState | null): boolean {
  // Horaires inconnus (appel en cours ou en échec) : on ne bloque personne,
  // le serveur reste l'arbitre à la commande.
  if (!ordering || ordering.open) return true;
  if (product.preorder_enabled) return true; // ne consomme aucun stock
  if (product.is_deposit) return true; // une consigne suit son contenant
  const ouvertes = ordering.always_open_category_ids || [];
  return !!(product.category_id && ouvertes.includes(product.category_id));
}

/**
 * Le jour de retrait, écrit pour un client : « jeudi 10 septembre ».
 *
 * Formaté depuis le PRÉFIXE date, jamais depuis `new Date(iso)` seul : la
 * colonne est un jour civil normalisé à minuit UTC, et le relire en heure
 * locale ferait basculer au jour précédent dans tout fuseau négatif. Midi
 * fixe le jour sans ambiguïté.
 */
export function formatJourRetrait(v?: string | null): string {
  if (!v) return '';
  const jour = String(v).slice(0, 10);
  const d = new Date(`${jour}T12:00:00`);
  if (isNaN(d.getTime())) return jour;
  return d.toLocaleDateString('fr-BE', {
    timeZone: 'Europe/Brussels',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/** « 14h » ou « 14h30 » — on n'écrit pas « 14:00 » à un client. */
function heureLisible(h: string): string {
  const [hh, mm] = h.split(':');
  const heures = String(Number(hh));
  return mm && mm !== '00' ? `${heures}h${mm}` : `${heures}h`;
}

/**
 * Une plage d'ouverture, écrite pour être lue : « jeudi de 14h à 18h », ou
 * « dimanche à partir de 7h30 — Marché de Mons, place Nervienne ».
 */
export function formatPlageHoraire(p: {
  day: number;
  from: string;
  to?: string | null;
  place?: string | null;
  note?: string | null;
}): string {
  const jour = JOURS[p.day] ?? '';
  const quand = p.to ? `de ${heureLisible(p.from)} à ${heureLisible(p.to)}` : (p.note || `à partir de ${heureLisible(p.from)}`);
  return p.place ? `${jour} ${quand} — ${p.place}` : `${jour} ${quand}`;
}

/** Où retirer un jour donné : le magasin, ou le marché le dimanche. */
export function lieuDeRetrait(
  horaires: Array<{ day: number; place?: string | null }> | undefined,
  isoOuJour?: string | null,
): string | null {
  if (!horaires?.length || !isoOuJour) return null;
  const d = new Date(`${String(isoOuJour).slice(0, 10)}T12:00:00`);
  if (isNaN(d.getTime())) return null;
  return horaires.find((h) => h.day === d.getDay())?.place || null;
}
